import { Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, map, of, switchMap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';

import { OmsAction } from '../actions/oms.action';
import { DraftAction } from '../actions/draft.action';
import { filterInvisibleStates } from '../../../tools';
import { OfflineAction } from '../actions/offline.action';
import * as orderSelector from '../selectors/order.selector';
import { AggregateState, OrderModelType } from '../../../constants';
import { IBaseOrderEntity, OMSOrder, OfflineOrder, Side } from '../../../modules/order';
import * as todayActivitySelector from '../../../state/orders/selectors/today-activity.selector';
export type OrderFilter = {
	showRed?: boolean;
	showGreen?: boolean;
	showDraft?: boolean;
	showDone?: boolean;
	searchInput?: string;
	symbolIsin?: string;
};

@Injectable({ providedIn: 'root' })
export class OrderStore {
	allOrders$ = this.store.select(orderSelector.getAllOrder);
	omsOrders$ = this.store.select(orderSelector.omsOrders);
	draftOrders$ = this.store.select(orderSelector.draftOrders);
	offlineOrders$ = this.store.select(orderSelector.offlineOrders);
	tradeLastUpdateTime$ = this.store.select(orderSelector.tradeLastUpdateTime);
	tradeLastUpdateTime = this.store.selectSignal(orderSelector.tradeLastUpdateTime);

	allOrders = toSignal(this.allOrders$);
	loading = this.store.selectSignal(orderSelector.getLoading);
	AlgoSuffixCharacters = this.store.selectSignal(orderSelector.algoSuffixCharacters);
	// omsOrders$ = this.store.select(orderSelector.getOmsOrder);

	toggleFilter$(filter?: OrderFilter) {
		const buyFilter = map((res: IBaseOrderEntity[]) => {
			if (filter?.showGreen == false) return res.filter(t => !t.isBuy);
			return res;
		});
		const sellFilter = map((res: IBaseOrderEntity[]) => {
			if (filter?.showRed == false) return res.filter(t => !t.isSell);
			return res;
		});
		const draftFilter = map((res: IBaseOrderEntity[]) => {
			if (filter?.showDraft == false) return res.filter(t => t.orderModelType !== OrderModelType.draft);
			return res;
		});
		const doneFilter = map((res: OMSOrder[]) => {
			if (filter?.showDone == false) return res.filter(t => t?.AggregateState !== AggregateState.Done);
			return res;
		});
		const searchFilter = map((res: IBaseOrderEntity[]) => {
			if (filter?.searchInput) return res.filter(t => t.symbolName.includes(filter?.searchInput));
			return res;
		});

		const symbolIsinFilter = map((res: IBaseOrderEntity[]) => {
			if (filter?.symbolIsin) return res.filter(t => t.symbolIsin === filter?.symbolIsin);
			return res;
		});

		return this.allOrders$
			.pipe(buyFilter, sellFilter, draftFilter, doneFilter, searchFilter, symbolIsinFilter)
			.pipe(
				map((res: any[]) => res.filter(order => filterInvisibleStates(order as OMSOrder)))
				// map(res => res.sort(this.sortOrders))
			);
	}
	tabFilter$(filter?: OrderFilter) {
		const draftFilter = map((res: IBaseOrderEntity[]) => {
			if (filter.showDone && filter.showDraft) return res;
			if (filter?.showDraft) return res.filter(t => t.orderModelType === OrderModelType.draft);
			if (filter?.showDraft === false) return res.filter(t => t.orderModelType !== OrderModelType.draft);
			return res;
		});
		const doneFilter = map((res: OMSOrder[]) => {
			if (filter.showDone && filter.showDraft) return res;
			if (filter?.showDone) return res.filter(t => t?.AggregateState === AggregateState.Done);
			if (filter?.showDone === false) return res.filter(t => t?.AggregateState !== AggregateState.Done);
			return res;
		});
		const searchFilter = map((res: IBaseOrderEntity[]) => {
			if (filter?.searchInput) return res.filter(t => t.symbolName.includes(filter?.searchInput));
			return res;
		});

		return this.allOrders$.pipe(draftFilter, doneFilter, searchFilter).pipe(
			map(res => res.filter(order => filterInvisibleStates(order as OMSOrder)))
			// map(res => res.sort(this.sortOrders))
		);
	}

	getOrders$(): Observable<IBaseOrderEntity[]>;
	getOrders$(ids: string[]): Observable<IBaseOrderEntity[]>;
	getOrders$(ids: string[], symbolIsin: string): Observable<IBaseOrderEntity[]>;
	getOrders$(ids: string[], symbolIsin: string, type: OrderModelType): Observable<IBaseOrderEntity[]>;
	getOrders$(ids?: string[], symbolIsin?: string, type?: OrderModelType): Observable<IBaseOrderEntity[]> {
		return this.store.select(orderSelector.getOrders(ids, symbolIsin, type));
	}
	getOrders(): IBaseOrderEntity[];
	getOrders(ids: string[]): IBaseOrderEntity[];
	getOrders(ids: string[], symbolIsin: string): IBaseOrderEntity[];
	getOrders(ids: string[], symbolIsin: string, type: OrderModelType): IBaseOrderEntity[];
	getOrders(ids?: string[], symbolIsin?: string, type?: OrderModelType): IBaseOrderEntity[] {
		return this.store.selectSignal(orderSelector.getOrders(ids, symbolIsin, type))();
	}
	getOmsOrder$(id: string): Observable<OMSOrder> {
		return this.store.select(orderSelector.getOmsOrder(id));
	}
	getOpenOrder$(symbolIsin: string, side: Side) {
		return this.store.select(orderSelector.getOpenOrder(symbolIsin, side));
	}
	getOmsOrder(id: string): OMSOrder {
		return this.store.selectSignal(orderSelector.getOmsOrder(id))();
	}
	getOmsOrderByHon(hon: number) {
		return this.store.selectSignal(orderSelector.getOmsOrderByHon(hon))();
	}
	getOfflineOrder(symbolIsin: string): OfflineOrder {
		return this.store.selectSignal(orderSelector.getOfflineOrder(symbolIsin))();
	}
	getOrder(id: string): IBaseOrderEntity {
		return this.store.selectSignal(orderSelector.getOrder(id))();
	}
	getOrderBySymbolIsin(symbolIsin: string): IBaseOrderEntity {
		return this.store.selectSignal(orderSelector.getOrderBySymbolIsin(symbolIsin))();
	}
	async getOrderIdsForDelete(ids?: string[], orderModelType?: OrderModelType) {
		return await firstValueFrom(this.store.select(orderSelector.getOrderIdsForDelete(ids, orderModelType)));
	}
	//HesamTodo should remove later
	batchCreated = toSignal(this.actions$.pipe(ofType(DraftAction.batchCreated)));
	batchDeleted = toSignal(this.actions$.pipe(ofType(DraftAction.batchDeleted)));

	batchCreated$ = this.actions$.pipe(ofType(DraftAction.batchCreated));
	batchDeleted$ = this.actions$.pipe(ofType(DraftAction.batchDeleted));
	createdOmsOrder$ = this.actions$.pipe(ofType(OmsAction.createdOms, OmsAction.add));
	omsRequestDone$ = this.actions$.pipe(
		ofType(OmsAction.requestDone),
		map(res => res?.value)
		// filter(Boolean)
	);
	draftRequestDone$ = this.actions$.pipe(
		ofType(DraftAction.requestDone),
		map(res => res.value)
		// filter(Boolean)
	);
	orderLoaded = toSignal(
		this.actions$.pipe(
			ofType(OmsAction.set, DraftAction.set, OfflineAction.set),
			switchMap(() => of(true))
		)
	);
	omsRequestDone = toSignal(
		this.actions$.pipe(
			ofType(OmsAction.requestDone),
			map(res => res?.value)
		)
	);
	draftRequestDone = toSignal(
		this.actions$.pipe(
			ofType(DraftAction.requestDone),
			map(res => res.value)
		)
	);
	deletedDraft$ = this.actions$.pipe(
		ofType(DraftAction.deletedDraft),
		map(res => res.id)
	);

	deletedOms$ = this.actions$.pipe(
		ofType(OmsAction.deletedOms),
		map(res => res.value)
	);

	offlineAdded$ = this.actions$.pipe(
		ofType(OfflineAction.add),
		map(res => res.value)
	);

	// todayActivityShouldCallIsins$ = this.store.select(todayActivitySelector.getTodayActivityShouldCallIsins);
	todayActivityData$ = this.store.select(todayActivitySelector.getTodayActivityData);
	constructor(
		private store: Store,
		private actions$: Actions
	) {}
}
